import React, { useState, useEffect } from 'react';
import { get, post, del } from '../utils/api';
import { useAuth } from '../context/AuthContext';
const fmt = n => '\u20a6' + Number(n||0).toLocaleString('en-NG');

export default function Payments() {
  const { user } = useAuth();
  const [data, setData] = useState({ data:[], count:0, total:0 });
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [printPayment, setPrintPayment] = useState(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const [settings, setSettings] = useState({});

  const load = () => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (from) qs.append('from', from);
    if (to) qs.append('to', to);
    get('/payments?'+qs.toString()).then(setData).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    get('/invoices').then(d => setInvoices(d.data||[]));
    get('/invoice-settings').then(setSettings);
  }, [from, to]);

  const filtered = search
    ? data.data.filter(p =>
        p.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
        p.invoice_number?.toLowerCase().includes(search.toLowerCase()) ||
        p.payment_number?.toLowerCase().includes(search.toLowerCase()))
    : data.data;

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this payment? The invoice balance will be reversed.')) return;
    try {
      await del('/payments/'+id);
      load();
    } catch (err) { alert(err.message); }
  };

  const openReceipt = (payment) => {
    setPrintPayment(payment);
  };

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">Payment Log</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>All customer payments received</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ Record Payment</button>
      </div>

      {/* Summary */}
      <div className="metrics" style={{gridTemplateColumns:'repeat(2,1fr)',marginBottom:20}}>
        <div className="tile">
          <div className="tile-lbl">Total Payments{from||to?' (filtered)':''}</div>
          <div className="tile-val pos">{fmt(data.total)}</div>
        </div>
        <div className="tile">
          <div className="tile-lbl">Number of Payments</div>
          <div className="tile-val">{data.count}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="search-bar">
        <input placeholder="Search by customer, invoice, ref..." value={search} onChange={e => setSearch(e.target.value)} style={{flex:1}} />
        <label style={{fontSize:12,color:'var(--ink-light)'}}>From</label>
        <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
        <label style={{fontSize:12,color:'var(--ink-light)'}}>To</label>
        <input type="date" value={to} onChange={e => setTo(e.target.value)} />
        {(from||to) && <button className="btn btn-ghost btn-sm" onClick={() => { setFrom(''); setTo(''); }}>Clear</button>}
      </div>

      <div className="tbl-wrap">
        {loading ? <div className="spinner"></div> : (
          <table>
            <thead>
              <tr>
                <th>Payment #</th>
                <th>Date</th>
                <th>Customer</th>
                <th>Invoice</th>
                <th>Method</th>
                <th>Reference</th>
                <th style={{textAlign:'right'}}>Amount</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0
                ? <tr><td colSpan="8"><div className="empty-state"><h3>No payments found</h3></div></td></tr>
                : filtered.map(p => (
                  <tr key={p.id}>
                    <td style={{fontWeight:500}}>{p.payment_number}</td>
                    <td>{p.date}</td>
                    <td>{p.customer_name}</td>
                    <td className="text-muted text-sm">{p.invoice_number}</td>
                    <td><span className="badge b-grey">{p.method}</span></td>
                    <td className="text-muted text-sm">{p.reference||'—'}</td>
                    <td className="amt" style={{color:'var(--paid)'}}>{fmt(p.amount)}</td>
                    <td>
                      <div className="flex-center gap-sm">
                        <button className="btn btn-outline btn-sm" onClick={() => openReceipt(p)}>Receipt</button>
                        {user?.role === 'super_admin' && (
                          <button className="btn btn-danger btn-sm" onClick={() => handleDelete(p.id)}>Delete</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        )}
      </div>

      {showAdd && (
        <AddPaymentModal
          invoices={invoices}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(); }} />
      )}

      {printPayment && (
        <ReceiptModal
          payment={printPayment}
          settings={settings}
          onClose={() => setPrintPayment(null)} />
      )}
    </div>
  );
}

function AddPaymentModal({ invoices, onClose, onSaved }) {
  const [form, setForm] = useState({
    invoice_id:'', date: new Date().toISOString().split('T')[0],
    amount:'', method:'transfer', reference:'', notes:'', bank_account_id:''
  });
  const [selectedInv, setSelectedInv] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [bankAccounts, setBankAccounts] = useState([]);

  useEffect(() => {
    get('/bank-accounts').then(d => {
      const accounts = d.data||[];
      setBankAccounts(accounts);
      // Each payment needs its actual receiving account.
    });
  }, []);

  const handleInvoiceChange = (id) => {
    const inv = invoices.find(i => i.id === id);
    setSelectedInv(inv||null);
    setForm({...form, invoice_id:id, amount: inv ? inv.balance_due : ''});
  };

  const submit = async () => {
    if (!form.invoice_id || !form.date || !form.amount || !form.method) {
      setError('Invoice, date, amount and method are required'); return;
    }
    if(!form.bank_account_id){setError('Select the account that received the payment');return;}
    setSaving(true);
    try {
      await post('/payments', form);
      onSaved();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const unpaidInvoices = invoices.filter(i => i.status !== 'paid' && i.status !== 'cancelled');

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:500}}>
        <div className="modal-hdr">
          <span className="modal-title">Record Payment</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error mb-md">{error}</div>}
          <div className="form-group">
            <label className="form-label">Invoice *</label>
            <select className="form-control" value={form.invoice_id} onChange={e => handleInvoiceChange(e.target.value)}>
              <option value="">Select invoice</option>
              {unpaidInvoices.map(i => (
                <option key={i.id} value={i.id}>
                  {i.invoice_number} — {i.customer_name} (Balance: {'₦'}{Number(i.balance_due).toLocaleString()})
                </option>
              ))}
            </select>
          </div>

          {selectedInv && (
            <div style={{background:'#F2EFE7',padding:12,borderRadius:8,marginBottom:16,fontSize:13}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                <span style={{color:'#8A8A8A'}}>Invoice Total</span>
                <span>{fmt(selectedInv.total)}</span>
              </div>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                <span style={{color:'#8A8A8A'}}>Already Paid</span>
                <span style={{color:'var(--paid)'}}>{fmt(selectedInv.amount_paid)}</span>
              </div>
              <div style={{display:'flex',justifyContent:'space-between',fontWeight:600}}>
                <span style={{color:'#8A8A8A'}}>Balance Due</span>
                <span style={{color:'#8F2E24',fontFamily:'Cormorant Garamond,serif',fontSize:16}}>{fmt(selectedInv.balance_due)}</span>
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Date *</label>
              <input type="date" className="form-control" value={form.date} onChange={e => setForm({...form, date:e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">Amount (N) *</label>
              <input type="number" className="form-control" value={form.amount} onChange={e => setForm({...form, amount:e.target.value})} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Method *</label>
              <select className="form-control" value={form.method} onChange={e => setForm({...form, method:e.target.value,bank_account_id:''})}>
                <option value="transfer">Bank Transfer</option>
                <option value="cash">Cash</option>
                <option value="pos">POS</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Reference</label>
              <input className="form-control" placeholder="Transaction ref, cheque no..." value={form.reference} onChange={e => setForm({...form, reference:e.target.value})} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Received Into</label>
            <select className="form-control" value={form.bank_account_id} onChange={e => setForm({...form, bank_account_id:e.target.value})}>
              <option value="">Select bank account</option>
              {bankAccounts.filter(a=>a.type===(form.method==='cash'?'cash':'bank')).map(a => (
                <option key={a.id} value={a.id}>{a.name} — {a.bank_name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <input className="form-control" value={form.notes} onChange={e => setForm({...form, notes:e.target.value})} />
          </div>
        </div>
        <div className="modal-ftr">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-gold" onClick={submit} disabled={saving}>
            {saving ? 'Recording...' : 'Record Payment'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReceiptModal({ payment, settings, onClose }) {
  const s = settings || {};

  const printReceipt = () => {
    const w = window.open('', '_blank');
    const fmt2 = n => '\u20a6' + Number(n||0).toLocaleString('en-NG');
    const logoHtml = s.logo_url
      ? `<img src="${s.logo_url}" style="width:60px;height:60px;object-fit:contain;margin-right:14px">`
      : '';

    w.document.write(`<!DOCTYPE html><html><head>
      <meta charset="utf-8"><title>Receipt ${payment.payment_number}</title>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600&family=Inter:wght@400;500;600&display=swap" rel="stylesheet">
      <style>body{font-family:Inter,sans-serif;margin:0;padding:0}@media print{body{margin:0}}</style>
      </head><body>
      <div style="max-width:480px;margin:0 auto;border:1px solid #EDE7D8;border-radius:10px;overflow:hidden">

        <!-- Header -->
        <div style="background:#556B2F;padding:20px 24px;display:flex;align-items:center">
          ${logoHtml}
          <div>
            <div style="font-family:Cormorant Garamond,serif;font-size:1.3rem;font-weight:600;color:#A8893D">${s.company_name||'BLOANS CREATIONS LTD'}</div>
            <div style="font-size:9px;letter-spacing:.1em;text-transform:uppercase;color:rgba(242,239,231,.5);margin-top:2px">${s.company_tagline||'Premium Jute & Luxury Gift Packaging'}</div>
          </div>
        </div>

        <!-- Receipt title -->
        <div style="background:#F2EFE7;padding:14px 24px;text-align:center;border-bottom:1px solid #DDD4C0">
          <div style="font-family:Cormorant Garamond,serif;font-size:1.4rem;font-weight:600;color:#3B2A21">PAYMENT RECEIPT</div>
          <div style="font-size:11px;color:#8A8A8A;margin-top:3px">${payment.payment_number}</div>
        </div>

        <!-- Payment details -->
        <div style="padding:20px 24px">
          <table style="width:100%;border-collapse:collapse;font-size:13px">
            <tr>
              <td style="padding:8px 0;border-bottom:1px solid #EDE7D8;color:#8A8A8A;width:40%">Date</td>
              <td style="padding:8px 0;border-bottom:1px solid #EDE7D8;font-weight:500">${payment.date}</td>
            </tr>
            <tr>
              <td style="padding:8px 0;border-bottom:1px solid #EDE7D8;color:#8A8A8A">Received From</td>
              <td style="padding:8px 0;border-bottom:1px solid #EDE7D8;font-weight:600;color:#3B2A21">${payment.customer_name}</td>
            </tr>
            <tr>
              <td style="padding:8px 0;border-bottom:1px solid #EDE7D8;color:#8A8A8A">Invoice</td>
              <td style="padding:8px 0;border-bottom:1px solid #EDE7D8">${payment.invoice_number}</td>
            </tr>
            <tr>
              <td style="padding:8px 0;border-bottom:1px solid #EDE7D8;color:#8A8A8A">Payment Method</td>
              <td style="padding:8px 0;border-bottom:1px solid #EDE7D8;text-transform:capitalize">${payment.method}</td>
            </tr>
            ${payment.reference ? `<tr>
              <td style="padding:8px 0;border-bottom:1px solid #EDE7D8;color:#8A8A8A">Reference</td>
              <td style="padding:8px 0;border-bottom:1px solid #EDE7D8">${payment.reference}</td>
            </tr>` : ''}
            ${payment.notes ? `<tr>
              <td style="padding:8px 0;border-bottom:1px solid #EDE7D8;color:#8A8A8A">Notes</td>
              <td style="padding:8px 0;border-bottom:1px solid #EDE7D8">${payment.notes}</td>
            </tr>` : ''}
          </table>

          <!-- Amount -->
          <div style="margin-top:20px;padding:16px;background:#F2EFE7;border-radius:8px;text-align:center">
            <div style="font-size:11px;color:#8A8A8A;text-transform:uppercase;letter-spacing:.1em;margin-bottom:6px">Amount Received</div>
            <div style="font-family:Cormorant Garamond,serif;font-size:2rem;font-weight:700;color:#556B2F">${fmt2(payment.amount)}</div>
          </div>

          <!-- Signature -->
          <div style="margin-top:20px;display:grid;grid-template-columns:1fr 1fr;gap:16px">
            <div style="text-align:center">
              <div style="height:40px;border-bottom:1px solid #DDD4C0;margin-bottom:6px"></div>
              <div style="font-size:10px;color:#8A8A8A">Customer Signature</div>
            </div>
            <div style="text-align:center">
              <div style="height:40px;border-bottom:1px solid #DDD4C0;margin-bottom:6px"></div>
              <div style="font-size:10px;color:#8A8A8A">Authorised Signature</div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div style="background:#556B2F;padding:12px 24px;text-align:center">
          <div style="font-size:11px;color:rgba(242,239,231,.6)">
            ${s.company_website||'bloanshandbags.com'} | ${s.company_phone||'08028708436'}
          </div>
          <div style="font-size:10px;color:rgba(242,239,231,.4);margin-top:3px">This is an official payment receipt</div>
        </div>
      </div>
      <script>window.onload=function(){window.print();}</script>
      </body></html>`);
    w.document.close();
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:420}}>
        <div className="modal-hdr">
          <span className="modal-title">Payment Receipt</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
        </div>
        <div className="modal-body">
          <div style={{background:'#F2EFE7',borderRadius:8,padding:16,marginBottom:16}}>
            <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:8}}>
              <span style={{color:'#8A8A8A'}}>Payment Ref</span>
              <strong>{payment.payment_number}</strong>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:8}}>
              <span style={{color:'#8A8A8A'}}>Customer</span>
              <strong>{payment.customer_name}</strong>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:8}}>
              <span style={{color:'#8A8A8A'}}>Invoice</span>
              <span>{payment.invoice_number}</span>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:8}}>
              <span style={{color:'#8A8A8A'}}>Date</span>
              <span>{payment.date}</span>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',fontSize:13}}>
              <span style={{color:'#8A8A8A'}}>Amount</span>
              <strong style={{fontFamily:'Cormorant Garamond,serif',fontSize:18,color:'#556B2F'}}>{fmt(payment.amount)}</strong>
            </div>
          </div>
          <button className="btn btn-primary" style={{width:'100%',justifyContent:'center',padding:12,fontSize:14}} onClick={printReceipt}>
            Print / Download Receipt
          </button>
          <p className="text-muted text-xs" style={{textAlign:'center',marginTop:10}}>
            In the print dialog choose "Save as PDF" to download
          </p>
        </div>
      </div>
    </div>
  );
}
